<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="900px"
    persistent
    @keydown.esc="close"
  >
    <v-card>
      <v-card-title>
        <v-icon left>mdi-account-details</v-icon>
        <span>{{ $t('onboarding.enrollment-details') }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" :aria-label="$t('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4" v-if="enrollment">
        <v-tabs v-model="activeTab" show-arrows>
          <v-tab>
            <v-icon left small>mdi-chart-timeline-variant</v-icon>
            {{ $t('onboarding.tabs.overview') }}
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-office-building</v-icon>
            {{ $t('onboarding.tabs.company-info') }}
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-credit-card</v-icon>
            {{ $t('onboarding.tabs.payment-mandate') }}
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-cog</v-icon>
            {{ $t('onboarding.tabs.technical') }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- Tab 1: Overview -->
          <v-tab-item>
            <v-card flat class="pa-4">
              <div class="mb-6 pb-4" style="border-bottom: 2px solid #e0e0e0;">
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon left color="primary">mdi-office-building</v-icon>
                    <h3 class="text-h6 mb-0">{{ enrollment.companyName }}</h3>
                  </div>
                  <div class="d-flex align-center text--secondary">
                    <v-icon left small>mdi-email</v-icon>
                    <span>{{ enrollment.email }}</span>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">{{ $t('status') }}</div>
                <v-chip small :color="statusColor(enrollment.status)" dark class="mb-3">
                  {{ $t(`onboarding.status.${enrollment.status}`) || enrollment.status }}
                </v-chip>
                <v-chip small :color="enrollmentTypeColor(enrollment)" :dark="isDarkChip(enrollment)" class="ml-2 mb-3">
                  {{ enrollmentTypeLabel(enrollment) }}
                </v-chip>
              </div>
              <v-row>
                <v-col>

                  <!-- Workflow Timeline -->
                  <div class="workflow-timeline mt-4">
                    <div class="text-subtitle-2 mb-3">{{ $t('onboarding.tabs.workflow-progress') }}</div>
                    <v-timeline dense>
                      <v-timeline-item
                          small
                          :color="hasReachedStatus('draft') ? 'green' : 'grey'"
                          :icon="hasReachedStatus('draft') ? 'mdi-check' : 'mdi-circle-outline'"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.created') }}</strong>
                          <div v-if="hasReachedStatus('draft')" class="text--secondary">
                            {{ formatDate(enrollment.createdAt) }}
                          </div>
                        </div>
                      </v-timeline-item>

                      <v-timeline-item
                          small
                          :color="hasReachedStatus('sent') ? 'green' : 'grey'"
                          :icon="hasReachedStatus('sent') ? 'mdi-check' : 'mdi-circle-outline'"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.email-sent') }}</strong>
                          <div v-if="enrollment.sentAt" class="text--secondary">
                            {{ formatDate(enrollment.sentAt) }}
                          </div>
                        </div>
                      </v-timeline-item>

                      <v-timeline-item
                          small
                          :color="hasReachedStatus('visited') ? 'green' : 'grey'"
                          :icon="hasReachedStatus('visited') ? 'mdi-check' : 'mdi-circle-outline'"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.link-visited') }}</strong>
                          <div v-if="enrollment.visitedAt" class="text--secondary">
                            {{ formatDate(enrollment.visitedAt) }}
                          </div>
                        </div>
                      </v-timeline-item>

                      <v-timeline-item
                          small
                          :color="hasReachedStatus('validated') ? 'green' : 'grey'"
                          :icon="hasReachedStatus('validated') ? 'mdi-check' : 'mdi-circle-outline'"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.info-validated') }}</strong>
                          <div v-if="enrollment.validatedAt" class="text--secondary">
                            {{ formatDate(enrollment.validatedAt) }}
                          </div>
                        </div>
                      </v-timeline-item>

                      <v-timeline-item
                          small
                          :color="hasReachedStatus('signed') ? 'green' : 'grey'"
                          :icon="hasReachedStatus('signed') ? 'mdi-check' : 'mdi-circle-outline'"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.mandate-signed') }}</strong>
                          <div v-if="enrollment.signedAt" class="text--secondary">
                            {{ formatDate(enrollment.signedAt) }}
                          </div>
                        </div>
                      </v-timeline-item>

                      <v-timeline-item
                          small
                          :color="hasReachedStatus('completed') ? 'green' : 'grey'"
                          :icon="hasReachedStatus('completed') ? 'mdi-check' : 'mdi-circle-outline'"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.payment-method-created') }}</strong>
                          <div v-if="enrollment.completedAt" class="text--secondary">
                            {{ formatDate(enrollment.completedAt) }}
                          </div>
                        </div>
                      </v-timeline-item>

                      <v-timeline-item
                          v-if="enrollment.status === 'converted'"
                          small
                          color="success"
                          icon="mdi-check-circle"
                      >
                        <div class="text-caption">
                          <strong>{{ $t('onboarding.workflow.converted') }}</strong>
                          <div v-if="enrollment.convertedAt" class="text--secondary">
                            {{ formatDate(enrollment.convertedAt) }}
                          </div>
                        </div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                </v-col>
                <v-divider vertical class="my-4"></v-divider>
                <v-col>
                  <div class="text-subtitle-2 mb-3">{{ $t('onboarding.public-link') }}</div>

                  <!-- QR Code Component -->
                  <qr-link-actions
                      :link="publicLink"
                      :size="300"
                      filename="enrollment-qrcode.png"
                  />
                </v-col>
              </v-row>

              <!-- Company Info Header -->
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Company Information -->
          <v-tab-item>
            <v-card flat class="pa-4">
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" style="width: 200px;">{{ $t('email') }}</td>
                      <td>{{ enrollment.email || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('company-name') }}</td>
                      <td>{{ enrollment.companyName || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('phone') }}</td>
                      <td>{{ enrollment.phone || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('siret') }}</td>
                      <td>{{ enrollment.siret || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('vat-number') }}</td>
                      <td>{{ enrollment.vatNumber || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('evco-id') }}</td>
                      <td>{{ enrollment.evcoId || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('account-number') }}</td>
                      <td>{{ enrollment.accountNumber || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('communication-language') }}</td>
                      <td>{{ formatLocale(enrollment.locale) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('address') }}</td>
                      <td>{{ formatAddress(enrollment.address) || '-' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Signatory Information (for SEPA mandates) -->
              <div v-if="enrollment.firstName || enrollment.lastName" class="mt-4">
                <v-divider class="mb-3"></v-divider>
                <div class="text-subtitle-2 mb-3">{{ $t('payment-method-ui.signatory-info') }}</div>
                <v-simple-table>
                  <template v-slot:default>
                    <tbody>
                      <tr v-if="enrollment.firstName">
                        <td class="font-weight-bold" style="width: 200px;">{{ $t('firstname') }}</td>
                        <td>{{ enrollment.firstName }}</td>
                      </tr>
                      <tr v-if="enrollment.lastName">
                        <td class="font-weight-bold">{{ $t('lastname') }}</td>
                        <td>{{ enrollment.lastName }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>

              <!-- Organization Membership (for customers with attachToOrganization) -->
              <div v-if="enrollment.attachToOrganization" class="mt-4">
                <v-divider class="mb-3"></v-divider>
                <div class="text-subtitle-2 mb-3">{{ $t('onboarding.membership') }}</div>
                <v-simple-table>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold" style="width: 200px;">{{ $t('onboarding.attach-to-organization-account') }}</td>
                        <td>{{ enrollment.attachToOrganization }}</td>
                      </tr>
                      <tr v-if="enrollment.memberType">
                        <td class="font-weight-bold">{{ $t('onboarding.member-type') }}</td>
                        <td>{{ enrollment.memberType }}</td>
                      </tr>
                      <tr v-if="enrollment.memberGroups">
                        <td class="font-weight-bold">{{ $t('onboarding.member-groups') }}</td>
                        <td>{{ enrollment.memberGroups }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Payment & Mandate -->
          <v-tab-item>
            <v-card flat class="pa-4">
              <div v-if="hasMandate">
                <div class="text-subtitle-2 mb-3">{{ $t('payment-method-ui.mandate') }}</div>
                <v-simple-table>
                  <template v-slot:default>
                    <tbody>
                      <tr v-if="enrollment.mandateReference">
                        <td class="font-weight-bold" style="width: 200px;">RUM</td>
                        <td>{{ enrollment.mandateReference }}</td>
                      </tr>
                      <tr v-if="enrollment.stripePaymentMethodId">
                        <td class="font-weight-bold">Stripe Payment Method ID</td>
                        <td>
                          <span class="text-caption text--secondary">{{ enrollment.stripePaymentMethodId }}</span>
                        </td>
                      </tr>
                      <tr v-if="enrollment.signedAt">
                        <td class="font-weight-bold">{{ $t('onboarding.workflow.mandate-signed') }}</td>
                        <td>{{ formatDate(enrollment.signedAt) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- Mandate Action Buttons -->
                <div class="d-flex flex-wrap gap-2 mt-4 mb-4">
                  <v-btn
                    v-if="enrollment.mandateUrl"
                    color="primary"
                    outlined
                    small
                    @click="viewStripeMandate"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    {{ $t('payment-method-ui.view-stripe-mandate') }}
                  </v-btn>

                  <v-btn
                    v-if="enrollment.mandateSnapshotId"
                    color="primary"
                    outlined
                    small
                    :loading="loadingSignedMandate"
                    @click="viewSignedMandate"
                  >
                    <v-icon left small>mdi-file-document-outline</v-icon>
                    {{ $t('payment-method-ui.view-zeborne-mandate') }}
                  </v-btn>
                </div>

                <!-- Payment Method Details -->
                <div v-if="enrollment.paymentMethod" class="mt-4">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-subtitle-2 mb-3">{{ $t('payment-method-ui.payment-method-details') }}</div>
                  <v-simple-table>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td class="font-weight-bold" style="width: 200px;">{{ $t('type') }}</td>
                          <td>{{ enrollment.paymentMethod.type || '-' }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">IBAN ({{ $t('payment-method-ui.iban') }})</td>
                          <td>****{{ enrollment.paymentMethod.last4 || '-' }}</td>
                        </tr>
                        <tr v-if="enrollment.paymentMethod.country">
                          <td class="font-weight-bold">{{ $t('country') }}</td>
                          <td>{{ enrollment.paymentMethod.country }}</td>
                        </tr>
                        <tr v-if="enrollment.paymentMethod.bankCode">
                          <td class="font-weight-bold">Bank Code</td>
                          <td>{{ enrollment.paymentMethod.bankCode }}</td>
                        </tr>
                        <tr v-if="enrollment.paymentMethod.branchCode">
                          <td class="font-weight-bold">Branch Code</td>
                          <td>{{ enrollment.paymentMethod.branchCode }}</td>
                        </tr>
                        <tr v-if="enrollment.paymentMethod.fingerprint">
                          <td class="font-weight-bold">Fingerprint</td>
                          <td>
                            <span class="text-caption text--secondary">{{ enrollment.paymentMethod.fingerprint }}</span>
                          </td>
                        </tr>
                        <tr v-if="enrollment.paymentMethod.billingDetails?.name">
                          <td class="font-weight-bold">{{ $t('payment-method-ui.billing-name') }}</td>
                          <td>{{ enrollment.paymentMethod.billingDetails.name }}</td>
                        </tr>
                        <tr v-if="enrollment.paymentMethod.billingDetails?.email">
                          <td class="font-weight-bold">{{ $t('payment-method-ui.contact-email') }}</td>
                          <td>{{ enrollment.paymentMethod.billingDetails.email }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>
              <div v-else class="text-center text--secondary py-8">
                <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-file-document-outline</v-icon>
                <p>{{ $t('onboarding.no-mandate-yet') }}</p>
              </div>
            </v-card>
          </v-tab-item>

          <!-- Tab 4: Technical -->
          <v-tab-item>
            <v-card flat class="pa-4">
              <!-- IDs Section -->
              <div class="text-subtitle-2 mb-3">IDs</div>
              <v-simple-table dense class="mb-4">
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" style="width: 220px;">Enrollment ID</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.id }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.id)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="enrollment.organizationId">
                      <td class="font-weight-bold">Organization ID (linked)</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.organizationId }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.organizationId)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="enrollment.createdOrganizationId">
                      <td class="font-weight-bold">Created Organization ID</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.createdOrganizationId }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.createdOrganizationId)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                        <v-btn
                          x-small
                          icon
                          color="green"
                          @click="goToOrganization(enrollment.createdOrganizationId)"
                          class="ml-1"
                        >
                          <v-icon x-small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="enrollment.createdCustomerId">
                      <td class="font-weight-bold">Created Customer ID</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.createdCustomerId }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.createdCustomerId)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                        <v-btn
                          x-small
                          icon
                          color="purple"
                          @click="goToCustomer(enrollment.createdCustomerId)"
                          class="ml-1"
                        >
                          <v-icon x-small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="enrollment.createdMemberId">
                      <td class="font-weight-bold">Created Member ID</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.createdMemberId }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.createdMemberId)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="enrollment.stripePaymentMethodId">
                      <td class="font-weight-bold">Stripe Payment Method ID</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.stripePaymentMethodId }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.stripePaymentMethodId)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="enrollment.mandateSnapshotId">
                      <td class="font-weight-bold">Mandate Snapshot ID</td>
                      <td>
                        <span class="text-caption text--secondary">{{ enrollment.mandateSnapshotId }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.mandateSnapshotId)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- General Info Section -->
              <v-divider class="my-4"></v-divider>
              <div class="text-subtitle-2 mb-3">{{ $t('onboarding.enrollment-info') }}</div>
              <v-simple-table dense class="mb-4">
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" style="width: 220px;">{{ $t('type') }}</td>
                      <td>
                        <v-chip x-small>
                          {{ enrollment.isProspect ? $t('onboarding.prospect') : $t('onboarding.organization') }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('status') }}</td>
                      <td>
                        <v-chip x-small :color="statusColor(enrollment.status)" dark>
                          {{ $t(`onboarding.status.${enrollment.status}`) || enrollment.status }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Locale</td>
                      <td>{{ formatLocale(enrollment.locale) }} ({{ enrollment.locale }})</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>


              <v-divider class="my-4"></v-divider>

              <!-- JWT Token Details -->
              <div class="text-subtitle-2 mb-3">Token Details</div>
              <v-simple-table dense class="mb-4">
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" style="width: 220px;">JWT Token</td>
                      <td>
                        <span class="text-caption text--secondary" style="word-break: break-all;">{{ enrollment.token }}</span>
                        <v-btn x-small icon @click="copyToClipboard(enrollment.token)" class="ml-1">
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('onboarding.token-expiration') }}</td>
                      <td>
                        {{ formatDate(enrollment.tokenExpiration) }}
                        <v-chip x-small :color="enrollment.isTokenValid ? 'green' : 'red'" dark class="ml-2">
                          {{ enrollment.isTokenValid ? $t('valid') : $t('expired') }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Workflow Dates Section -->
              <v-divider class="my-4"></v-divider>
              <div class="text-subtitle-2 mb-3">{{ $t('onboarding.tabs.workflow-progress') }} - Dates</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" style="width: 220px;">{{ $t('created') }}</td>
                      <td>{{ formatDate(enrollment.createdAt) || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('onboarding.workflow.email-sent') }}</td>
                      <td>{{ formatDate(enrollment.sentAt) || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('onboarding.workflow.link-visited') }}</td>
                      <td>{{ formatDate(enrollment.visitedAt) || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('onboarding.workflow.info-validated') }}</td>
                      <td>{{ formatDate(enrollment.validatedAt) || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('onboarding.workflow.mandate-signed') }}</td>
                      <td>{{ formatDate(enrollment.signedAt) || '-' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('onboarding.workflow.payment-method-created') }}</td>
                      <td>{{ formatDate(enrollment.completedAt) || '-' }}</td>
                    </tr>
                    <tr v-if="enrollment.convertedAt">
                      <td class="font-weight-bold">{{ $t('onboarding.workflow.converted') }}</td>
                      <td>{{ formatDate(enrollment.convertedAt) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="canSendEmail"
          text
          small
          color="primary"
          @click="sendEmail"
        >
          <v-icon left small>mdi-email-send</v-icon>
          {{ $t('onboarding.send-email') }}
        </v-btn>
        <v-btn
          v-if="canConvert"
          text
          small
          color="green"
          @click="convertToOrganization"
        >
          <v-icon left small>mdi-check-circle</v-icon>
          {{ convertLabel }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text small @click="close">
          {{ $t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Stripe Mandate Dialog (with iframe) -->
    <v-dialog v-model="stripeMandateDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-file-pdf-box</v-icon>
          <span>{{ $t('payment-method-ui.view-stripe-mandate') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeStripeMandateDialog" :aria-label="$t('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <iframe
            v-if="stripeMandateUrl"
            :src="stripeMandateUrl"
            style="width: 100%; height: 600px; border: none;"
            frameborder="0"
          ></iframe>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="closeStripeMandateDialog">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ZEborne Signed Mandate Dialog -->
    <v-dialog v-model="signedMandateDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-file-document-outline</v-icon>
          <span>{{ $t('payment-method-ui.view-zeborne-mandate') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeSignedMandateDialog" :aria-label="$t('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-alert v-if="signedMandateError" type="error" dense outlined class="mb-4">
            {{ signedMandateError }}
          </v-alert>

          <v-progress-linear v-if="loadingSignedMandate" indeterminate color="primary" class="mb-4"/>

          <div v-if="signedMandateHtml && !loadingSignedMandate" class="signed-mandate-content pa-4" style="max-height: 600px; overflow-y: auto; border: 1px solid #ccc; border-radius: 4px;">
            <div v-html="signedMandateHtml"></div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="closeSignedMandateDialog">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import QrLinkActions from '@/components/shared/QrLinkActions.vue'

export default {
  name: 'EnrollmentDetailDialog',

  components: {
    QrLinkActions
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    enrollment: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      activeTab: 0,
      loadingSignedMandate: false,
      signedMandateDialog: false,
      signedMandateHtml: null,
      signedMandateError: null,
      stripeMandateDialog: false,
      stripeMandateUrl: null
    }
  },

  computed: {
    hasAddress() {
      return this.enrollment?.address &&
             (this.enrollment.address.street ||
              this.enrollment.address.city ||
              this.enrollment.address.zipcode ||
              this.enrollment.address.country)
    },

    hasMandate() {
      // Check if enrollment has reached a status where mandate exists
      const hasValidStatus = ['signed', 'completed', 'converted'].includes(this.enrollment?.status)
      const hasMandateData = this.enrollment?.mandateReference ||
                             this.enrollment?.mandateUrl ||
                             this.enrollment?.mandateSnapshotId

      return hasValidStatus && hasMandateData
    },

    canSendEmail() {
      // Email sending is allowed for all statuses except terminal ones
      const blockedStatuses = ['completed', 'converted', 'canceled', 'failed', 'expired']
      return !blockedStatuses.includes(this.enrollment?.status)
    },

    canConvert() {
      // Enrollment can be converted only if:
      // - Status is 'completed' (mandate signed and payment method created)
      // - Has a type ('organization' or 'customer')
      // - Not already converted
      return this.enrollment?.status === 'completed' &&
             (this.enrollment?.type === 'organization' || this.enrollment?.type === 'customer') &&
             !this.enrollment?.createdOrganizationId &&
             !this.enrollment?.createdCustomerId &&
             !this.enrollment?.customerId &&
             !this.enrollment?.organizationId
    },

    convertLabel() {
      if (this.enrollment?.type === 'customer') {
        return this.$t('onboarding.convert-to-customer')
      }
      return this.$t('onboarding.convert-to-organization')
    },

    publicLink() {
      if (!this.enrollment?.token) return ''
      return `${window.location.origin}/onboarding?token=${this.enrollment.token}&locale=${this.enrollment.locale}`
    }
  },

  watch: {

  },

  methods: {
    close() {
      this.$emit('input', false)
    },

    sendEmail() {
      this.$emit('send-email', this.enrollment)
    },

    convertToOrganization() {
      this.$emit('convert', this.enrollment)
    },

    hasReachedStatus(targetStatus) {
      const statusOrder = ['draft', 'sent', 'visited', 'validated', 'mandate_previewed', 'signed', 'completed', 'converted']
      const currentIndex = statusOrder.indexOf(this.enrollment?.status)
      const targetIndex = statusOrder.indexOf(targetStatus)
      return currentIndex >= targetIndex
    },

    viewStripeMandate() {
      if (!this.enrollment?.mandateUrl) return
      this.stripeMandateUrl = this.enrollment.mandateUrl
      this.stripeMandateDialog = true
    },

    closeStripeMandateDialog() {
      this.stripeMandateDialog = false
      this.stripeMandateUrl = null
    },

    async viewSignedMandate() {
      const snapshotId = this.enrollment?.mandateSnapshotId
      if (!snapshotId) return

      // Open dialog and start loading
      this.signedMandateDialog = true
      this.loadingSignedMandate = true
      this.signedMandateError = null
      this.signedMandateHtml = null

      try {
        const locale = this.$i18n?.locale || 'fr'
        const result = await PaymentOnboardingRepository.getSignedMandateView(
          snapshotId,
          { locale }
        )

        // Display the HTML in the dialog
        if (result.renderedHtml || result.html) {
          this.signedMandateHtml = result.renderedHtml || result.html
        } else if (result.url) {
          // If the backend returns a URL instead, open in new window
          window.open(result.url, '_blank')
          this.signedMandateDialog = false
        } else {
          throw new Error('No HTML or URL returned from signed mandate API')
        }
      } catch (err) {
        this.signedMandateError = err?.response?.data?.message || err?.message || 'Error loading signed mandate'
      } finally {
        this.loadingSignedMandate = false
      }
    },

    closeSignedMandateDialog() {
      this.signedMandateDialog = false
      this.signedMandateHtml = null
      this.signedMandateError = null
    },

    statusColor(status) {
      const colors = {
        draft: 'grey',
        sent: 'blue',
        visited: 'cyan',
        validated: 'teal',
        mandate_previewed: 'indigo',
        signed: 'purple',
        completed: 'green',
        converted: 'success',
        failed: 'red',
        expired: 'orange'
      }
      return colors[status] || 'grey'
    },

    formatDate(val) {
      if (!val) return ''
      try {
        return new Date(val).toLocaleString()
      } catch (e) {
        return String(val)
      }
    },

    formatAddress(address) {
      if (!address) return ''
      const parts = []
      if (address.street) parts.push(address.street)
      if (address.zipcode || address.city) {
        parts.push(`${address.zipcode || ''} ${address.city || ''}`.trim())
      }
      if (address.country) parts.push(address.country)
      return parts.join(', ')
    },

    formatLocale(locale) {
      const locales = {
        fr: 'Français',
        en: 'English',
        nl: 'Nederlands'
      }
      return locales[locale] || locale || 'Français'
    },

    copyToClipboard(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.link-copied'))
      }).catch((err) => {
        this.$dialog?.notify?.error && this.$dialog.notify.error('Copy failed')
      })
    },

    goToOrganization(organizationId) {
      if (!organizationId) return
      // Open in new window as requested by user
      const route = this.$router.resolve({
        name: 'organizations.detail',
        params: { id: organizationId }
      })
      window.open(route.href, '_blank')
    },

    goToCustomer(customerId) {
      if (!customerId) return
      // Open in new window
      const route = this.$router.resolve({
        name: 'customer.detail',
        params: { id: customerId }
      })
      window.open(route.href, '_blank')
    },

    enrollmentTypeLabel(item) {
      const isConverted = !!(item.createdOrganizationId || item.organizationId || item.createdCustomerId || item.customerId)

      if (item.type === 'organization') {
        return isConverted ? this.$t('onboarding.organization') : this.$t('onboarding.prospect-organization')
      }

      if (item.type === 'customer') {
        return isConverted ? this.$t('onboarding.customer') : this.$t('onboarding.prospect-customer')
      }

      return this.$t('onboarding.prospect')
    },

    enrollmentTypeColor(item) {
      if (item.type === 'organization') {
        return 'blue'
      }

      if (item.type === 'customer') {
        return 'purple'
      }

      return 'grey'
    },

    isDarkChip(item) {
      const color = this.enrollmentTypeColor(item)
      return color === 'blue' || color === 'purple'
    }
  }
}
</script>

<style scoped>
/* Dialog specific styles */
</style>
